import { Markdown } from '@/components/shared/Markdown';
import { ReportMediaCard } from '@/components/shared/ReportMediaCard';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { KPI } from '@/hooks/useCampaigns';
import { useReport, useReportPeriods } from '@/hooks/useReport';
import { motion } from 'framer-motion';
import { ReactElement } from 'react';
import { Navigate, useParams } from 'react-router-dom';

export const ReportsPage = (): ReactElement => {
  const { id, kpi } = useParams();
  const currentKPI = (kpi as KPI) || KPI.AWARENESS;

  const { data: reports, isLoading: isLoadingReports } = useReportPeriods();
  const { data: report, isLoading: isLoadingReport } = useReport(
    id || undefined,
    currentKPI,
  );

  // Handle initial load - redirect to most recent report
  if (!isLoadingReports && !id && reports?.length) {
    return <Navigate to={`/reports/${reports[0].id}/${currentKPI}`} replace />;
  }

  if (id && isLoadingReport) {
    return <SkeletonLoader />;
  }

  return (
    <>
      {report && (
        <div className="flex-1 flex flex-col gap-6">
          <div className="rounded-lg border p-6">
            <Markdown>{report.summary}</Markdown>
          </div>

          <div className="grid grid-cols-5 gap-6">
            {report.top_media.map((media, index) => (
              <div className="col-span-1">
                <motion.div
                  key={index}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.1 }}>
                  <ReportMediaCard
                    key={media.id}
                    number={index + 1}
                    media={media}
                    isTopPerformer={index === 0}
                  />
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

const SkeletonLoader = () => {
  return (
    <div className="flex-1 flex flex-col gap-6">
      <Card className="p-6">
        <div className="space-y-4">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </Card>

      <div className="grid grid-cols-5 gap-6">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="col-span-1">
            <Card className="p-4 space-y-3">
              <Skeleton className="aspect-square" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
};
